import { services } from '../generated/services';
import type { CometCargo } from '../simulation/comet-recovery';

export interface SettingsSnapshot {
  readonly paused: boolean;
}

export interface SettingsCallbacks {
  readonly onChange: (settings: SettingsSnapshot) => void;
  readonly onRestart: () => void;
  readonly onFireComet: (cargo: CometCargo) => void;
}

export class SettingsController {
  private readonly events = new AbortController();
  private readonly panel = requireElement<HTMLElement>('settings-panel');
  private readonly openButton =
    requireElement<HTMLButtonElement>('open-settings');
  private readonly closeButton =
    requireElement<HTMLButtonElement>('close-settings');
  private readonly pauseButton = requireElement<HTMLButtonElement>('pause');
  private readonly restartButton = requireElement<HTMLButtonElement>('restart');
  private readonly fireCometButton =
    requireElement<HTMLButtonElement>('fire-comet');
  private readonly cometCargo =
    requireElement<HTMLSelectElement>('comet-cargo');
  private paused = false;
  private interfaceHidden = false;
  private lastRevealTap = -Infinity;

  public constructor(private readonly callbacks: SettingsCallbacks) {
    this.openButton.addEventListener('click',
(event) => {
      const now = performance.now();
      // Keyboard activation remains a single action; touch/mouse requires two.
      if (event.detail === 0 || now - this.lastRevealTap < 450) {
        this.lastRevealTap = -Infinity;
        this.open();
      } else this.lastRevealTap = now;
    },
{ signal: this.events.signal });
    const links = document.getElementById('service-links');
    if (links) {
      links.replaceChildren(...services.map(service => {
        const link = document.createElement('a');
        link.href = service.url;
        link.textContent = service.label;
        return link;
      }));
    }
    this.closeButton.addEventListener('click',
() => this.close(),
{ signal: this.events.signal });
    this.pauseButton.addEventListener('click',
() => this.togglePause(),
{ signal: this.events.signal });
    this.restartButton.addEventListener('click',
() => callbacks.onRestart(),
{ signal: this.events.signal });
    this.fireCometButton.addEventListener('click',
() =>
      callbacks.onFireComet(this.cometCargo.value as CometCargo),
{ signal: this.events.signal }
    );
    document.addEventListener('keydown',
(event) => this.onKeyDown(event),
{ signal: this.events.signal });
  }

  public snapshot(): SettingsSnapshot {
    return {
      paused: this.paused,
    };
  }

  public dispose(): void { this.events.abort(); }

  public setPaused(paused: boolean): void {
    this.paused = paused;
    this.pauseButton.textContent = paused ? 'Resume' : 'Pause';
    document.body.dataset.paused = String(paused);
    this.emit();
  }

  public open(): void {
    this.interfaceHidden = false;
    document.body.classList.remove('interface-hidden');
    this.panel.hidden = false;
    document.body.classList.add('settings-open');
    this.openButton.setAttribute('aria-expanded', 'true');
    this.closeButton.focus({ preventScroll: true });
  }

  public close(): void {
    this.lastRevealTap = -Infinity;
    const restoreFocus = this.panel.contains(document.activeElement);
    this.panel.hidden = true;
    document.body.classList.remove('settings-open');
    this.openButton.setAttribute('aria-expanded', 'false');
    if (restoreFocus && !this.interfaceHidden) {
      this.openButton.focus({ preventScroll: true });
    }
  }

  private togglePause(): void {
    this.setPaused(!this.paused);
  }

  private toggleInterface(): void {
    this.interfaceHidden = !this.interfaceHidden;
    document.body.classList.toggle('interface-hidden', this.interfaceHidden);
    if (this.interfaceHidden) {
      this.close();
    }
  }

  private emit(): void {
    this.callbacks.onChange(this.snapshot());
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented || event.repeat || event.isComposing ||
      event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable) ||
      (event.key === ' ' && target instanceof Element &&
        target.closest('button, summary, a[href], [role="button"]') !== null)
    ) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === ' ' || key === 'p') {
      event.preventDefault();
      this.togglePause();
    } else if (key === 'r') {
      this.callbacks.onRestart();
    } else if (key === 'h') {
      this.toggleInterface();
    } else if (key === 's') {
      this.open();
    } else if (key === 'escape') {
      this.close();
    }
  }
}

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (element === null) {
    throw new Error(`Required settings element #${id} is missing.`);
  }
  return element as T;
}
